import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CharacterPartType } from "../../dto/CharacterPartDTO";
import { RootState } from "../Store";
import { CharacterPartGenderedPairDTO } from "../../dto/CharacterPartGenderedPairDTO";
import { CharacterPartGenderedPairDetailsDTO } from "../../dto/CharacterPartGenderedPairDetailsDTO";

export interface CharacterPartLibraryState {
  isInitialized: boolean;
  pagedCharacterParts: CharacterPartGenderedPairDTO[];
  selectedCharacterPart: CharacterPartGenderedPairDTO | null;
  selectedCharacterPartDetails: CharacterPartGenderedPairDetailsDTO | null;
  cachedCharacterPartDetails: Record<
    string,
    CharacterPartGenderedPairDetailsDTO
  >;
  cachedCharacterPartRenders: Record<string, string>;
}

const initialCharacterPartLibraryState: CharacterPartLibraryState = {
  isInitialized: false,
  pagedCharacterParts: [],
  selectedCharacterPart: null,
  selectedCharacterPartDetails: null,
  cachedCharacterPartDetails: {},
  cachedCharacterPartRenders: {},
};

export const characterPartLibrarySlice = createSlice({
  name: "characterPartLibrary",
  initialState: initialCharacterPartLibraryState,
  reducers: {
    initializeCharacterPartLibrary: (
      state,
      action: PayloadAction<{
        totalCount: number;
        firstPage: CharacterPartGenderedPairDTO[];
      }>
    ) => {
      const { totalCount, firstPage } = action.payload;
      state.isInitialized = true;
      state.pagedCharacterParts = new Array(totalCount).fill({
        name: "unloaded",
        characterPartType: CharacterPartType.Misc,
        malePartId: "unloaded",
        femalePartId: "unloaded",
      } as CharacterPartGenderedPairDTO);

      for (let i = 0; i < firstPage.length; i++) {
        state.pagedCharacterParts[i] = firstPage[i];
      }
    },
    updateCharacterPartLibrary: (
      state,
      action: PayloadAction<{
        pageNumber: number;
        page: CharacterPartGenderedPairDTO[];
      }>
    ) => {
      const { pageNumber, page } = action.payload;
      state.pagedCharacterParts.splice(pageNumber * 10, 10, ...page);
    },
    setSelectedCharacterPart: (
      state,
      action: PayloadAction<CharacterPartGenderedPairDTO>
    ) => {
      state.selectedCharacterPart = action.payload;
    },
    setSelectedCharacterPartDetails: (
      state,
      action: PayloadAction<CharacterPartGenderedPairDetailsDTO>
    ) => {
      state.selectedCharacterPartDetails = action.payload;
    },
    setCachedCharacterPartDetails: (
      state,
      action: PayloadAction<{
        characterPartName: string;
        characterPartDetails: CharacterPartGenderedPairDetailsDTO;
      }>
    ) => {
      state.cachedCharacterPartDetails[action.payload.characterPartName] =
        action.payload.characterPartDetails;
    },
    setCachedCharacterPartRender: (
      state,
      action: PayloadAction<{
        characterPartName: string;
        downloadUri: string;
      }>
    ) => {
      state.cachedCharacterPartRenders[action.payload.characterPartName] =
        action.payload.downloadUri;
    },
    resetCharacterPartLibrary: () => initialCharacterPartLibraryState,
  },
});

export const {
  initializeCharacterPartLibrary,
  updateCharacterPartLibrary,
  setSelectedCharacterPart,
  setSelectedCharacterPartDetails,
  setCachedCharacterPartDetails,
  setCachedCharacterPartRender,
  resetCharacterPartLibrary,
} = characterPartLibrarySlice.actions;

export const selectPagedCharacterParts = (state: RootState) =>
  state.characterPartLibrary.pagedCharacterParts;

export const selectSelectedCharacterPart = (state: RootState) =>
  state.characterPartLibrary.selectedCharacterPart;

export const selectSelectedCharacterPartDetails = (state: RootState) =>
  state.characterPartLibrary.selectedCharacterPartDetails;

export const selectCachedCharacterPartDetailsByName = (
  state: RootState,
  characterPartName: string
) => state.characterPartLibrary.cachedCharacterPartDetails[characterPartName];

export const selectCachedCharacterPartDetails = (state: RootState) =>
  state.characterPartLibrary.cachedCharacterPartDetails;

export const selectCachedCharacterPartRenderByName = (
  state: RootState,
  characterPartName: string
) => state.characterPartLibrary.cachedCharacterPartRenders[characterPartName];

export default characterPartLibrarySlice.reducer;
